import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "./clinicApi";
import "./clinicWeekly.css";
import ScheduleModal from "./ScheduleModal";
import ConfirmModal from "./ConfirmModal";
import InfoModal from "./InfoModal";

/* ========= utils: DÙNG CHUNG VỚI TV ========= */
const dowVN = (d) =>
  ["Chủ Nhật", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"][
    new Date(d).getDay()
  ];

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// ✅ KHÔNG dùng toISOString → tránh lệch múi giờ
const toISO = (d) => {
  if (!d) return "";
  if (typeof d === "string") {
    // "2025-12-02" hoặc "2025-12-02T00:00:00"
    return d.slice(0, 10);
  }
  const dt = new Date(d);
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const fmtDMY = (d) => {
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(
    dt.getMonth() + 1
  ).padStart(2, "0")}`;
};

// ✅ Lấy THỨ 2 của tuần chứa ngày d (0h)
const getMonday = (d) => {
  const x = new Date(d);
  const day = x.getDay(); // 0=CN, 1=Thứ2, ...
  const diff = day === 0 ? -6 : 1 - day; // Nếu CN lùi 6 ngày, còn lại lùi tới Thứ 2
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
};

export default function ClinicWeeklySchedule() {
  const [rooms, setRooms] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [schedules, setSchedules] = useState([]);

  // ✅ anchorDate = THỨ 2 của tuần hiện tại (giống kiểu logic tuần ban đầu)
  const [anchorDate, setAnchorDate] = useState(() => getMonday(new Date()));

  // ✅ Danh sách 7 ngày: Thứ 2 → Chủ Nhật
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(anchorDate, i)),
    [anchorDate]
  );

  const [form, setForm] = useState({
    roomId: "",
    doctorId: "",
    nurseId: "",
    caKham: "Sáng",
    ghiChu: "Làm việc",
    ngay: toISO(new Date()),
  });
  const [note, setNote] = useState("");

  const [newRoom, setNewRoom] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSchedule, setModalSchedule] = useState(null);

  const [confirm, setConfirm] = useState({
    open: false,
    title: "",
    message: "",
    onOk: null,
  });
  const [info, setInfo] = useState({
    open: false,
    title: "",
    message: "",
  });

  const [flashKey, setFlashKey] = useState("");
  const wrapRef = useRef(null);
  const cellRefs = useRef({});
  const setCellRef = (key) => (el) => {
    if (el) cellRefs.current[key] = el;
  };

  // === LOAD DỮ LIỆU BAN ĐẦU ===
  useEffect(() => {
    refreshAll();
  }, []);

  const refreshAll = async () => {
    const s = await api.schedules.list();

    // ✅ Chuẩn hóa dữ liệu BE (giống TV)
    const formatted = s.map((x) => ({
      ...x,
      ngayISO: toISO(x.ngay), // BE đã trả "yyyy-MM-dd"
      caText: x.buoi === 0 ? "Sáng" : "Chiều",
    }));

    const phongMap = new Map();
    const bsMap = new Map();
    const ddMap = new Map();

    formatted.forEach((x) => {
      if (x.tenPhongKham)
        phongMap.set(x.id_PhongKham, {
          id: x.id_PhongKham,
          tenPhong: x.tenPhongKham,
        });
      if (x.tenBS) bsMap.set(x.maSoBS, { id: x.maSoBS, hoTen: x.tenBS });
      if (x.tenDD) ddMap.set(x.maSoDD, { id: x.maSoDD, hoTen: x.tenDD });
    });

    setRooms([...phongMap.values()]);
    setDoctors([...bsMap.values()]);
    setNurses([...ddMap.values()]);
    setSchedules(formatted);
  };

  // === MAP DỮ LIỆU PHÒNG + NGÀY ===
  const dataMap = useMemo(() => {
    const map = {};
    rooms.forEach((r) => (map[r.id] = {}));
    schedules.forEach((s) => {
      const rid = s.id_PhongKham;
      if (!rid) return;
      const key = s.ngayISO; // "yyyy-MM-dd"
      if (!map[rid][key]) map[rid][key] = [];
      map[rid][key].push(s);
    });
    return map;
  }, [rooms, schedules]);

  const isOffLike = (v) => (v || "").toLowerCase().includes("ngh");
  const isChuaDangKy = (v) => (v || "").toLowerCase().includes("chưa đăng");

  // === BUILD PAYLOAD GỬI LÊN BE ===
  const buildPayload = () => {
    const off = isOffLike(form.ghiChu);
    return {
      maSoBS: off ? null : form.doctorId,
      maSoDD: off ? null : form.nurseId,
      buoi: form.caKham === "Sáng" ? 0 : 1,
      ngay: form.ngay, // "yyyy-MM-dd"
      id_PhongKham: form.roomId,
      tenPhongKham:
        rooms.find((r) => String(r.id) === String(form.roomId))?.tenPhong ||
        "",
      nguoi_UD: "00130",
      ghiChu: note.trim()
        ? `${form.ghiChu} - ${note.trim()}`
        : form.ghiChu,
    };
  };

  // === LƯU LỊCH KHÁM ===
  const saveSchedule = async () => {
    if (!form.roomId || !form.ngay || !form.caKham) {
      setInfo({
        open: true,
        title: "Thiếu thông tin",
        message: "Vui lòng chọn Phòng, Ngày và Ca.",
      });
      return;
    }
    if (!isOffLike(form.ghiChu) && (!form.doctorId || !form.nurseId)) {
      setInfo({
        open: true,
        title: "Thiếu thông tin",
        message: "Vui lòng chọn Bác sĩ và Điều dưỡng.",
      });
      return;
    }

    const payload = buildPayload();
    await api.schedules.create(payload);
    await refreshAll();
    setNote("");
  };

  const pickByCa = (list, ca) => list.filter((s) => s.caText === ca);

  const renderCell = (arr, key) => {
    const hasChua = arr.some((s) =>
      (s.ghiChu || "").toLowerCase().includes("chưa")
    );
    const hasNghi = arr.some((s) =>
      (s.ghiChu || "").toLowerCase().includes("ngh")
    );
    const tdClass = `cell ${
      hasChua ? "status-red" : hasNghi ? "status-yellow" : ""
    } ${flashKey === key ? "flash-cell" : ""}`;

    if (hasChua || hasNghi) {
      const label = hasChua ? "Chưa đăng kí" : "Nghỉ";
      return (
        <td
          ref={setCellRef(key)}
          className={tdClass}
          onClick={() => arr[0] && openModal(arr[0])}
        >
          <div className="fill-label">{label}</div>
        </td>
      );
    }

    return (
      <td ref={setCellRef(key)} className={tdClass}>
        {arr.map((s, idx) => (
          <span
            key={idx}
            className="txt doc-name"
            onClick={() => openModal(s)}
            title={s.tenBS || ""}
          >
            {s.tenBS}
          </span>
        ))}
      </td>
    );
  };

  // === MODAL HANDLERS ===
  const openModal = (s) => {
    setModalSchedule(s);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalSchedule(null);
  };
  const handleDelete = async (id) => {
    await api.schedules.remove(id);
    await refreshAll();
    closeModal();
  };

  return (
    <div className="clinic-schedule">
      {/* === HEADER / TOOLBAR === */}
      <div className="toolbar ui-card">
        <div className="nav">
          {/* ✅ Điều hướng THEO TUẦN (7 ngày) */}
          <button
            className="btn ghost"
            onClick={() => setAnchorDate(addDays(anchorDate, -7))}
          >
            ⟵ Tuần trước
          </button>

          <button
            className="btn ghost"
            onClick={() => setAnchorDate(getMonday(new Date()))}
          >
            Tuần này
          </button>

          <button
            className="btn ghost"
            onClick={() => setAnchorDate(addDays(anchorDate, 7))}
          >
            Tuần sau ⟶
          </button>

          <span className="week-range">
            Từ: {fmtDMY(weekDays[0])} — {fmtDMY(weekDays[6])}
          </span>
        </div>

        {/* === FORM NHẬP LỊCH === */}
        <div className="pretty-form-card">
          <div className="form pretty-form">
            <div className="add-block">
              <input
                className="inp"
                placeholder="Phòng (A101)"
                value={newRoom}
                onChange={(e) => setNewRoom(e.target.value)}
              />
              <button
                className="btn add"
                onClick={async () => {
                  if (newRoom.trim()) {
                    await api.rooms.create({ tenPhong: newRoom.trim() });
                    setNewRoom("");
                    await refreshAll();
                  }
                }}
              >
                Thêm Phòng Khám
              </button>
            </div>

            <div className="selects-block">
              <select
                className="inp same"
                value={form.roomId}
                onChange={(e) =>
                  setForm({ ...form, roomId: e.target.value })
                }
              >
                <option value="">Chọn phòng</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.tenPhong}
                  </option>
                ))}
              </select>

              <select
                className="inp same"
                value={form.doctorId}
                onChange={(e) =>
                  setForm({ ...form, doctorId: e.target.value })
                }
              >
                <option value="">Chọn bác sĩ</option>
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.hoTen}
                  </option>
                ))}
              </select>

              <select
                className="inp same"
                value={form.nurseId}
                onChange={(e) =>
                  setForm({ ...form, nurseId: e.target.value })
                }
              >
                <option value="">Chọn điều dưỡng</option>
                {nurses.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.hoTen}
                  </option>
                ))}
              </select>

              <select
                className="inp same"
                value={form.caKham}
                onChange={(e) =>
                  setForm({ ...form, caKham: e.target.value })
                }
              >
                <option>Sáng</option>
                <option>Chiều</option>
              </select>

              <select
                className="inp same"
                value={form.ghiChu}
                onChange={(e) =>
                  setForm({ ...form, ghiChu: e.target.value })
                }
              >
                <option>Làm việc</option>
                <option>Nghỉ</option>
                <option>Chưa đăng kí</option>
              </select>

              <input
                className="inp same"
                type="date"
                value={form.ngay}
                onChange={(e) =>
                  setForm({ ...form, ngay: e.target.value })
                }
              />

              <input
                className="inp note-input same"
                placeholder="Ghi chú (VD: đổi phòng...)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />

              <button className="btn save save-align-right" onClick={saveSchedule}>
                Lưu lịch
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* === BẢNG LỊCH === */}
      <div className="table-wrap" ref={wrapRef}>
        <table className="fixed hvv">
          <thead>
            <tr className="day-head">
              <th rowSpan={2} className="left-sticky">
                Phòng khám
              </th>
              {weekDays.map((d, i) => (
                <th key={i} colSpan={2}>
                  <div>{dowVN(d)}</div>
                  <div>{fmtDMY(d)}</div>
                </th>
              ))}
            </tr>
            <tr>
              {weekDays.map((_, i) => (
                <React.Fragment key={i}>
                  <th className="subhead">Sáng</th>
                  <th className="subhead">Chiều</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>

          <tbody>
            {rooms.map((r) => (
              <tr key={r.id}>
                <td className="room-name left-sticky">{r.tenPhong}</td>
                {weekDays.map((d, i) => {
                  const iso = toISO(d); // "yyyy-MM-dd"
                  const list = dataMap[r.id]?.[iso] || [];
                  const sang = pickByCa(list, "Sáng");
                  const chieu = pickByCa(list, "Chiều");
                  return (
                    <React.Fragment key={i}>
                      {renderCell(sang, `${r.id}-${iso}-Sáng`)}
                      {renderCell(chieu, `${r.id}-${iso}-Chiều`)}
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* === POPUPS === */}
 <ScheduleModal
  open={modalOpen}
  schedule={modalSchedule}
  onClose={closeModal}
  onDelete={handleDelete}
/>

      <ConfirmModal
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        onOk={confirm.onOk}
        onCancel={() =>
          setConfirm((c) => ({ ...c, open: false }))
        }
      />

      <InfoModal
        open={info.open}
        title={info.title}
        message={info.message}
        onClose={() =>
          setInfo((i) => ({ ...i, open: false }))
        }
      />
    </div>
  );
}
